package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.intelligence.ConciergeHandoff
import com.steamstreet.vegasful.browser.appScope
import com.steamstreet.vegasful.browser.spinnerSvg
import com.steamstreet.vegasful.css.VegasfulStyles
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.web.SEARCH_RESULT_COUNT_ATTRIBUTE
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.analytics.entityIdOf
import com.steamstreet.vegasful.web.analytics.entityTypeOf
import com.steamstreet.vegasful.web.analytics.pageTypeOf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.css.*
import kotlinx.css.properties.TextDecoration
import kotlinx.html.a
import kotlinx.html.div
import kotlinx.html.dom.append
import kotlinx.html.dom.create
import kotlinx.html.small
import kotlinx.html.span
import kotlinx.html.js.span
import kotlinx.html.unsafe
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.url.URL
import kotlin.time.Duration.Companion.milliseconds

/**
 * Initialize the global search.
 *
 * A search box can also offer the concierge, which the home page's does. Such a box carries
 * `data-concierge`, and while its `data-mode` is `ask` the box stops suggesting and sends what is
 * typed to the concierge instead of the results page. In search mode it still offers the concierge
 * as the first suggestion when what was typed reads like a question rather than a name. The
 * `home-hero` module owns the switch between the two; this module only reads the attributes.
 */
fun globalSearch(element: Element) {
    val input = (element.querySelector("input") as? HTMLInputElement)
    val offersConcierge = element.hasAttribute("data-concierge")
    fun askingConcierge() = offersConcierge && element.getAttribute("data-mode") == "ask"

    // The concierge reads the time frame from the question itself, so the box's "Tonight" or
    // "This weekend" setting is added to the question unless the question already says it.
    fun withTimeFrame(question: String): String {
        val timeFrame = element.getAttribute("data-when")?.takeIf { it.isNotBlank() } ?: return question
        return if (question.contains(timeFrame, ignoreCase = true)) question else "$question ($timeFrame)"
    }
    fun navigateToSearch(text: String) {
        // The query itself is never reported — only its length. A search box is somewhere people
        // type things about themselves, and `docs/analytics-plan.md` keeps free text out of the
        // analytics stream on purpose. `results_count` is unknown here; the results page reports
        // its own `search_performed` once it knows.
        Analytics.track(
            AnalyticsEvent.SEARCH_PERFORMED,
            AnalyticsParam.QUERY_LENGTH to text.length,
            AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_GLOBAL,
            AnalyticsParam.PAGE_TYPE to pageTypeOf(window.location.pathname)
        )
        document.location?.href = "${document.location?.origin}/search?searchText=${encodeURIComponent(text)}"
    }

    // Render the suggestion dropdown as a direct child of <body> (a "portal") so it escapes any
    // ancestor stacking context or overflow clipping and reliably floats above page content.
    // Its position is derived from the input's viewport rect each time it is shown.
    val dropdown = (document.createElement("div") as HTMLElement).apply {
        setAttribute("style", "position:fixed;z-index:2147483000;display:none;")
    }
    document.body?.appendChild(dropdown)

    // A suggestion being followed is the outcome of the typeahead, and the only part of it worth
    // an event: reporting a `search_performed` per keystroke would bury the real searches.
    dropdown.addEventListener("click", { event ->
        val row = (event.target as? Element)?.closest(".typeahead-row") as? HTMLAnchorElement
        if (row != null) {
            val rows = dropdown.querySelectorAll(".typeahead-row").asList()
            Analytics.track(
                AnalyticsEvent.SEARCH_RESULT_TAPPED,
                AnalyticsParam.ENTITY_TYPE to entityTypeOf(row.pathname),
                AnalyticsParam.ENTITY_ID to entityIdOf(row.pathname),
                AnalyticsParam.POSITION to rows.indexOf(row),
                AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_TYPEAHEAD
            )
        }
    })

    // A box can name a wider element to line the suggestions up with than the input itself: the
    // home hero's box holds a button or two beside its input, and the suggestions span all of it.
    val anchor = element.querySelector("[data-search-anchor]") as? HTMLElement
    if (offersConcierge) dropdown.classList.add("concierge-dropdown")

    fun positionDropdown() {
        val rect = (anchor ?: input ?: return).getBoundingClientRect()
        dropdown.style.left = "${rect.left}px"
        dropdown.style.top = "${rect.bottom + if (anchor != null) 10 else 0}px"
        dropdown.style.width = "${rect.width}px"
    }

    fun hideDropdown() {
        dropdown.style.display = "none"
    }

    fun showDropdown() {
        if (dropdown.querySelector(".typeahead-row, .concierge-nudge") != null) {
            positionDropdown()
            dropdown.style.display = "block"
        }
    }

    // Debounce keystrokes so we hit the typeahead endpoint at most once per pause in typing.
    var searchJob: Job? = null
    fun scheduleTypeahead(text: String) {
        searchJob?.cancel()
        if (text.length < 2 || askingConcierge()) {
            dropdown.innerHTML = ""
            hideDropdown()
            return
        }
        searchJob = appScope.launch {
            delay(250.milliseconds)
            val html = Account.load("/search/typeahead?searchText=${encodeURIComponent(text)}")
            dropdown.innerHTML = html ?: ""
            if (offersConcierge && looksLikeQuestion(text)) {
                addConciergeNudge(dropdown, text)
            }
            // The snippet always comes back as a full document; only show the dropdown when it
            // actually contains suggestion rows, otherwise it would render as an empty box.
            if (dropdown.querySelector(".typeahead-row, .concierge-nudge") != null) {
                showDropdown()
            } else {
                dropdown.innerHTML = ""
                hideDropdown()
            }
        }
    }

    input?.addEventListener("input", {
        scheduleTypeahead((it.target as? HTMLInputElement)?.value.orEmpty())
    })

    fun submit(value: String?) {
        if (value.isNullOrBlank()) return
        if (askingConcierge()) {
            hideDropdown()
            ConciergeHandoff.ask(withTimeFrame(value))
        } else {
            navigateToSearch(value)
        }
    }

    input?.addEventListener("keydown", { event ->
        val value = (event.target as? HTMLInputElement)?.value
        when ((event as KeyboardEvent).keyCode) {
            13 -> submit(value)  // Enter -> full results page, or the concierge
            27 -> hideDropdown()  // Escape
        }
    })

    input?.addEventListener("focus", { showDropdown() })
    // Delay the hide so a click on a suggestion registers before the dropdown is removed.
    input?.addEventListener("blur", {
        appScope.launch {
            delay(150.milliseconds)
            hideDropdown()
        }
    })

    // Keep the portalled dropdown aligned with the input as the page scrolls or resizes.
    val reposition: (org.w3c.dom.events.Event) -> Unit = {
        if (dropdown.style.display == "block") {
            positionDropdown()
        }
    }
    window.addEventListener("scroll", reposition, true)
    window.addEventListener("resize", reposition)

    // A box with its own submit button marks it; the older boxes use their search icon.
    (element.querySelector("[data-search-submit]") ?: element.querySelector("span"))?.addEventListener("click", {
        submit(input?.value)
    })

    val searchText = document.location?.href?.let { URL(it) }?.searchParams?.get("searchText")
    if (searchText != null) {
        input?.value = searchText
    }

    input?.focus()
}

/**
 * Whether [text] reads like a question for the concierge rather than the name of something to
 * look up: four or more words, or a question mark at the end. Names are rarely that long, and the
 * nudge it produces sits above the normal suggestions rather than replacing them, so a wrong guess
 * costs one extra row.
 */
private fun looksLikeQuestion(text: String): Boolean {
    val trimmed = text.trim()
    return trimmed.split(Regex("\\s+")).count { it.isNotEmpty() } >= 4 || trimmed.endsWith("?")
}

/**
 * Put an "Ask the concierge" row at the top of the suggestions, or on its own when there are
 * none. Following it asks the concierge the question as typed.
 */
private fun addConciergeNudge(dropdown: HTMLElement, text: String) {
    val nudge = document.create.a(href = ConciergeHandoff.CONCIERGE_PATH, classes = "concierge-nudge") {
        css {
            display = Display.grid
            put("grid-template-columns", "30px minmax(0, 1fr)")
            gap = 12.px
            alignItems = Align.center
            padding = Padding(10.px, 14.px)
            color = VegasfulStyles.textColor
            textDecoration = TextDecoration.none
            background = "linear-gradient(90deg, rgba(201, 0, 199, .3), rgba(201, 0, 199, .08))"
            borderBottom = Border(1.px, BorderStyle.solid, VegasfulStyles.pink.withAlpha(.3))
        }
        span {
            css {
                width = 30.px
                height = 30.px
                borderRadius = 8.px
                display = Display.inlineFlex
                alignItems = Align.center
                justifyContent = JustifyContent.center
                backgroundColor = Color("#ff7dfe")
                color = Color("#2a0030")
            }
            unsafe {
                +CONCIERGE_BELL_ICON
            }
        }
        span {
            css {
                minWidth = 0.px
            }
            div {
                css {
                    fontSize = 15.px
                    fontWeight = FontWeight.w600
                }
                +"Ask the concierge"
            }
            small {
                css {
                    display = Display.block
                    fontSize = 12.5.px
                    color = Color("#e7c9ea")
                    overflow = Overflow.hidden
                    put("text-overflow", "ellipsis")
                    whiteSpace = WhiteSpace.nowrap
                }
                +"\u201C${text.trim()}\u201D"
            }
        }
    }
    nudge.addEventListener("click", { event ->
        event.preventDefault()
        ConciergeHandoff.ask(text)
    })

    val box = dropdown.querySelector(".typeahead-row")?.parentElement
    if (box != null) {
        box.insertBefore(nudge, box.firstChild)
    } else {
        val wrapper = document.create.div {}
        wrapper.appendChild(nudge)
        dropdown.innerHTML = ""
        dropdown.appendChild(wrapper)
    }
}

/** The concierge's desk bell, as drawn on the home hero's button. */
private const val CONCIERGE_BELL_ICON =
    """<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 17a6 6 0 0 1 12 0"/><path d="M12 11V8.5"/><path d="M10.5 8.5h3"/><path d="M4 17h16"/><path d="M3 20h18"/></svg>"""

/**
 * Executes a search and sets the results
 */
fun searchResults(element: Element) {
    val query = element.getAttribute("data-search-input")
    if (!query.isNullOrBlank()) {
        appScope.launch {
            val resultsContainer = element.querySelector(".search-results-container")
            var done = false

            launch(Dispatchers.Main) {
                delay(700.milliseconds)
                if (!done) {
                    resultsContainer?.append {
                        span {
                            css {
                                fontSize = 2.em
                            }
                            unsafe {
                                +spinnerSvg()
                            }
                        }
                    }
                }
            }

            val result = Account.load("/search/results?searchText=${encodeURIComponent(query)}")
            done = true
            if (result != null) {
                resultsContainer?.let {
                    it.innerHTML = result
                }
            }

            // Reported once the results are in, so that `results_count` is a fact rather than a
            // guess — the server writes the count onto the fragment it rendered. A failed load
            // reports the search too, with no count, since a search that errored still happened.
            Analytics.track(
                AnalyticsEvent.SEARCH_PERFORMED,
                AnalyticsParam.QUERY_LENGTH to query.length,
                AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_RESULTS,
                AnalyticsParam.RESULTS_COUNT to resultCount(resultsContainer)
            )
            if (result == null) {
                Analytics.apiError(SEARCH_TYPE_RESULTS, "search results did not load")
            }

            // A card on the results page is the same event as a suggestion in the dropdown, told
            // apart by `search_type`.
            resultsContainer?.addEventListener("click", { event ->
                val anchor = (event.target as? Element)?.closest("a[href]") as? HTMLAnchorElement
                val type = anchor?.pathname?.let { entityTypeOf(it) }
                if (anchor != null && type != null && type != "unknown") {
                    Analytics.track(
                        AnalyticsEvent.SEARCH_RESULT_TAPPED,
                        AnalyticsParam.ENTITY_TYPE to type,
                        AnalyticsParam.ENTITY_ID to entityIdOf(anchor.pathname),
                        AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_RESULTS
                    )
                }
            })
        }
    }
}

/** How many results the server said the injected fragment holds, or null if it did not say. */
private fun resultCount(container: Element?): Int? =
    container?.querySelector("[$SEARCH_RESULT_COUNT_ATTRIBUTE]")
        ?.getAttribute(SEARCH_RESULT_COUNT_ATTRIBUTE)?.toIntOrNull()

/** The search box in the site header, which navigates to the results page. */
private const val SEARCH_TYPE_GLOBAL = "global"

/** The as-you-type suggestion dropdown. */
private const val SEARCH_TYPE_TYPEAHEAD = "typeahead"

/** The full results page. */
private const val SEARCH_TYPE_RESULTS = "results"